import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import AppDatatable from '../../../components/AppDatatable';
import AccessDeniedState from '../../../components/AccessDeniedState';
import OpenFormDatePicker from '../../../components/form/OpenFormDatePicker';
import RichTextEditor, { RichTextContent } from '../../../components/form/RichTextEditor';
import EditSubmittedForm from './ShuttleBusEditSubmittedForm';
import { getLoggedInUser, hasModulePermission } from '../../../utils/permissions';
import useRecordLock from '../../../hooks/useRecordLock';
import { getDateRangeParams } from '../../../utils/dateRange';
import { openDocumentViewer } from '../../../utils/documentViewer';

const SUBMITTED_FORMS_PERMISSION_KEYS = ['Submited Forms', 'Submitted Forms'];
const ALL_LOCATIONS = 'All Locations';
const DETAIL_DIALOG_EXCLUDED_KEYS = new Set(['uploaded_files', 'retained_uploaded_files', 'retainedUploadedFiles']);
const CLAIM_STATUS_OPTIONS = [
  'Initial Review',
  'Filing a claim',
  'Establishing Liability',
  'Appraisal Requested',
  'Estimate Requested',
  'Repairs Approved',
  'Waiting for vehicle repair',
  'Charged',
  'Demand Sent',
  'Follow up',
  'Settlement Review',
  'Waiting for payment',
  'Payment Details Verification',
  'Waiting for vehicle sale',
  'Other',
];
const INSURANCE_ROUTE_OPTIONS = ['Routes CDW', 'Personal Insurance', 'Credit Card Insurance', 'OTA'];
const PARTY_AT_FAULT_OPTIONS = ['Renter', '3rd Party'];
const LIABILITY_ACCEPTED_OPTIONS = ["Renter's Ins", "3rd Party's Personal Ins"];
const RENTER_INSURANCE_TYPE_OPTIONS = ['Personal', 'OTA', 'Credit card'];
const RENTER_INSURANCE_NAME_LABELS = {
  Personal: "Renter's Personal Insurance Name",
  OTA: "Renter's OTA Insurance Name",
  'Credit card': "Renter's Credit Card Insurance Name",
};
const PAYER_OPTIONS = ['Renter', "Renter's Ins", '3rd Party', "3rd Party's Ins", 'Other'];
const REPAIRS_APPROVED_AT_OPTIONS = ['Body Shop', 'In-house', 'Not repairing due to minor damage', 'Other'];
const VEHICLE_LOCATION_OPTIONS = [
  'At location - not yet sent to body shop',
  'At body shop getting repaired',
  'Repaired and returned to location',
];
const PAYMENT_MODE_OPTIONS = ['Charged Renter', '3rd Party', 'Bank/Wire Transfer', 'Credit Card', 'Check1', 'Check2', 'None'];
const COLLECTION_REASON_OPTIONS = ['Customer not responding', 'Ins denied claim'];
const DEMAND_PACKAGE_ITEMS = [
  { key: 'demandLetter', label: 'Demand Letter' },
  { key: 'signedRentalAgreement', label: 'Signed R/A (Rental Agreement)' },
  { key: 'damagePhotos', label: 'Damage Photos' },
  { key: 'damageIncidentReport', label: 'Damage/Incident Report' },
  { key: 'aob', label: 'AOB (Assignment of Benefits)' },
  { key: 'towImpoundBill', label: 'Tow Bill / Impound Bill' },
  { key: 'repairEstimateAppraisalAcv', label: 'Repair Estimate / Appraisal / ACV' },
  { key: 'salvage', label: 'Salvage' },
  { key: 'closedRentalAgreement', label: 'Closed R/A' },
];

const buildUploadedFileUrl = (baseURL, file = {}, download = false) => {
  const normalizedBaseURL = String(baseURL || '').replace(/\/$/, '');
  const normalizedPath = String(file.path || file.saved_name || '').replace(/\\/g, '/');
  const fileName = normalizedPath.split('/').filter(Boolean).pop();

  if (!normalizedBaseURL || !fileName) return '';

  const url = `${normalizedBaseURL}/open-form-upload/${encodeURIComponent(fileName)}`;
  return download ? `${url}?download=1` : url;
};

const viewAllUploadedFiles = (baseURL, files = []) => {
  openDocumentViewer(files.map((file, index) => ({
    name: String(file?.original_name || file?.saved_name || `Document ${index + 1}`).replace(/[<>&"']/g, ''),
    url: buildUploadedFileUrl(baseURL, file),
    contentType: file?.content_type,
  })));
};

const createProcessingForm = (row = {}) => ({
  rentalAgreementNumber: row.rentalAgreementNumber || '',
  claim_status: CLAIM_STATUS_OPTIONS.includes(row.claim_status)
    ? row.claim_status
    : (row.claim_status ? 'Other' : 'Initial Review'),
  claim_status_other: row.claim_status_other
    || (row.claim_status && !CLAIM_STATUS_OPTIONS.includes(row.claim_status) ? row.claim_status : ''),
  insurance_route: row.insurance_route || '',
  appraisal_requested_date: row.appraisal_requested_date || '',
  estimate_requested_date: row.estimate_requested_date || '',
  insurance_claim_number: row.insurance_claim_number || '',
  adjuster_name: row.adjuster_name || '',
  adjuster_phone: row.adjuster_phone || '',
  adjuster_email: row.adjuster_email || '',
  party_at_fault: row.party_at_fault || '',
  party_at_fault_name: row.party_at_fault_name || '',
  liability_accepted_by_insurance: row.liability_accepted_by_insurance || '',
  liability_insurance_name: row.liability_insurance_name || '',
  renter_insurance_type: row.renter_insurance_type || '',
  renter_insurance_name: row.renter_insurance_name || '',
  payer: PAYER_OPTIONS.includes(row.payer) ? row.payer : (row.payer ? 'Other' : ''),
  payer_other: row.payer_other || (row.payer && !PAYER_OPTIONS.includes(row.payer) ? row.payer : ''),
  payer_charged: row.payer_charged || '',
  payer_charged_amount: row.payer_charged_amount || '',
  repairs_approved_at: REPAIRS_APPROVED_AT_OPTIONS.includes(row.repairs_approved_at) ? row.repairs_approved_at : (row.repairs_approved_at ? 'Other' : ''),
  repairs_approved_at_other: row.repairs_approved_at_other || (row.repairs_approved_at && !REPAIRS_APPROVED_AT_OPTIONS.includes(row.repairs_approved_at) ? row.repairs_approved_at : ''),
  body_shop_name: row.body_shop_name || '',
  amount_approved: row.amount_approved || '',
  parts_cost_included: row.parts_cost_included || '',
  vehicle_location: row.vehicle_location || '',
  repairs_approved_at_2: REPAIRS_APPROVED_AT_OPTIONS.includes(row.repairs_approved_at_2) ? row.repairs_approved_at_2 : (row.repairs_approved_at_2 ? 'Other' : ''),
  repairs_approved_at_other_2: row.repairs_approved_at_other_2 || (row.repairs_approved_at_2 && !REPAIRS_APPROVED_AT_OPTIONS.includes(row.repairs_approved_at_2) ? row.repairs_approved_at_2 : ''),
  body_shop_name_2: row.body_shop_name_2 || '',
  amount_approved_2: row.amount_approved_2 || '',
  parts_cost_included_2: row.parts_cost_included_2 || '',
  vehicle_location_2: row.vehicle_location_2 || '',
  follow_up_entries: Array.isArray(row.follow_up_entries) ? row.follow_up_entries : [],
  follow_up_date: '',
  follow_up_notes: '',
  internal_note_entries: Array.isArray(row.internal_note_entries) && row.internal_note_entries.length
    ? row.internal_note_entries
    : (String(row.internal_notes || '').trim() ? [{
      id: 'legacy-internal-note',
      note: row.internal_notes,
      created_by: row.updated_by || row.created_by || '',
      created_datetime: row.updated_datetime || row.created_datetime || '',
    }] : []),
  internal_notes: '',
  original_demand_amount: row.original_demand_amount || '',
  settlement_offer_amount: row.settlement_offer_amount || '',
  settlement_decision: row.settlement_decision || '',
  team_lead_approval_status: row.team_lead_approval_status || '',
  approved_by: row.approved_by || '',
  deductible_applied: row.deductible_applied || '',
  deductible_applied_amount: row.deductible_applied_amount || '',
  charged_deductible: row.charged_deductible || '',
  mode_of_payment: row.mode_of_payment || '',
  payment_amount: row.payment_amount || '',
  check_received_date: row.check_received_date || '',
  check_amount: row.check_amount || '',
  check_received_insurance_company: row.check_received_insurance_company || '',
  check1_received_date: row.check1_received_date || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_received_date : ''),
  check1_amount: row.check1_amount || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_amount : ''),
  check1_received_insurance_company: row.check1_received_insurance_company || (['Check', 'Check1'].includes(row.mode_of_payment) ? row.check_received_insurance_company : ''),
  check2_received_date: row.check2_received_date || (row.mode_of_payment === 'Check2' ? row.check_received_date : ''),
  check2_amount: row.check2_amount || (row.mode_of_payment === 'Check2' ? row.check_amount : ''),
  check2_received_insurance_company: row.check2_received_insurance_company || (row.mode_of_payment === 'Check2' ? row.check_received_insurance_company : ''),
  collections_reason: row.collections_reason || '',
  collections_amount: row.collections_amount || '',
  collections_date_added: row.collections_date_added || '',
  demand_package: {
    demandLetter: Boolean(row?.demand_package?.demandLetter),
    signedRentalAgreement: Boolean(row?.demand_package?.signedRentalAgreement),
    damagePhotos: Boolean(row?.demand_package?.damagePhotos),
    damageIncidentReport: Boolean(row?.demand_package?.damageIncidentReport),
    aob: Boolean(row?.demand_package?.aob),
    towImpoundBill: Boolean(row?.demand_package?.towImpoundBill),
    repairEstimateAppraisalAcv: Boolean(row?.demand_package?.repairEstimateAppraisalAcv),
    salvage: Boolean(row?.demand_package?.salvage),
    closedRentalAgreement: Boolean(row?.demand_package?.closedRentalAgreement),
  },
});

const hasProcessingStarted = (row = {}) => {
  const demandPackage = row?.demand_package || {};

  return Boolean(
    row.insurance_route ||
    row.appraisal_requested_date ||
    row.estimate_requested_date ||
    row.insurance_claim_number ||
    row.adjuster_name ||
    row.party_at_fault ||
    row.liability_accepted_by_insurance ||
    row.payer ||
    row.repairs_approved_at ||
    row.vehicle_location ||
    (Array.isArray(row.follow_up_entries) && row.follow_up_entries.length > 0) ||
    row.original_demand_amount ||
    row.settlement_offer_amount ||
    row.settlement_decision ||
    row.team_lead_approval_status ||
    row.deductible_applied ||
    row.mode_of_payment ||
    row.collections_reason ||
    Object.values(demandPackage).some(Boolean)
  );
};

const isClaimCompleted = (row = {}) => Boolean(row?.closure_completed || row?.claim_status === 'Completed');

const getProcessingIdentifier = (row = {}) => {
  if (isClaimCompleted(row)) {
    return { label: 'Completed', className: 'completed' };
  }

  if (hasProcessingStarted(row)) {
    return { label: 'In Process', className: 'in-process' };
  }

  return { label: 'Yet to Process', className: 'yet-to-process' };
};

const normalizeUserLocations = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : '-';
  }

  const text = String(value ?? '').trim();
  return text || '-';
};

const formatDateTime = (value) => {
  if (!value) return '-';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return formatValue(value);
  }

  return parsed.toLocaleString();
};

const parseDateFilterValue = (value, endOfDay = false) => {
  if (!value) return null;
  const parsed = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

const isWithinDateRange = (value, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;

  const recordTime = parsed.getTime();
  const fromTime = parseDateFilterValue(fromDate);
  const toTime = parseDateFilterValue(toDate, true);

  return (!fromTime || recordTime >= fromTime) && (!toTime || recordTime <= toTime);
};

const isInvalidDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return false;
  const fromTime = parseDateFilterValue(fromDate);
  const toTime = parseDateFilterValue(toDate, true);
  return Boolean(fromTime && toTime && fromTime > toTime);
};

const sortFollowUpEntries = (entries = []) =>
  [...(Array.isArray(entries) ? entries : [])].sort((left, right) => {
    const leftDate = new Date(left?.created_datetime || left?.follow_up_date || 0).getTime();
    const rightDate = new Date(right?.created_datetime || right?.follow_up_date || 0).getTime();
    return (Number.isNaN(rightDate) ? 0 : rightDate) - (Number.isNaN(leftDate) ? 0 : leftDate);
  });

const shouldShowPayerCharge = (payer) => payer === 'Renter' || payer === '3rd Party';
const shouldShowPaymentAmount = (mode) => ['Charged Renter', '3rd Party', 'Bank/Wire Transfer', 'Credit Card'].includes(mode);
const showsCheck1 = (mode) => ['Check', 'Check1'].includes(mode);
const showsCheck2 = (mode) => mode === 'Check2';

const formatDetailValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => formatDetailValue(item)).join(', ') : '-';
  }

  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value)
      .filter(([, nestedValue]) => formatValue(nestedValue) !== '-')
      .map(([key, nestedValue]) => `${key}: ${formatDetailValue(nestedValue)}`);
    return entries.length ? entries.join(' | ') : '-';
  }

  return formatValue(value);
};

const toTitleCase = (key = '') =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

// Keep this summary aligned with the fields and category names in the opening form.
const REPORTED_OPEN_FORM_FIELDS = {
  'Getting Started': new Set(['seriousInjury', 'reportingEmployeeName', 'reportingEmployeeId', 'branchLocationCode', 'reportDateTime', 'reportDateTimeTimezone', 'customerReportedDateTime', 'customerReportedDateTimeTimezone', 'claimPriorityLevel', 'claimPriorityLevelOther', 'claimSource', 'claimSourceOther']),
  'Rental Agreement Details': new Set(['rentalAgreementNumber', 'tireGlassWaiver', 'authorizedDriverName', 'driverAtTimeOfAccident', 'driverAtTimeOfAccidentOther', 'customerPhone', 'preAuthorizationAmount', 'ldwStatus', 'ldwDeductibleAmount', 'ldwDeductibleAmountOther', 'customerInsuranceCarrier', 'insuranceType', 'insuranceTypeOther', 'customerPolicyNumber', 'claimOwningBranch', 'rentalStartDate', 'rentalReturnDate', 'originalRentalReturnDate', 'relationshipToClaim', 'relationshipOther', 'email', 'cellPhone', 'dealershipClaim', 'insuredCompanyName', 'parentCompanyName', 'routesPolicyNumber', 'siteCode', 'insuredCompanyAddress', 'zipCode', 'city', 'state', 'country', 'insuredCompanyPhone', 'insuredCompanyExtension', 'primaryInsuredContact', 'primaryContactName', 'primaryContactEmail', 'primaryContactPhone', 'primaryContactExtension', 'primaryContactCellPhone', 'sameAsCellPhone']),
  'Accident Overview': new Set(['accidentDateTime', 'accidentDateTimeTimezone', 'accidentDate', 'accidentTime', 'sameAsInsuredLocation', 'accidentLocation', 'gpsCoordinates', 'gpsLatitude', 'gpsLongitude', 'accidentAddress', 'accidentZipCode', 'accidentCity', 'accidentState', 'accidentCountry', 'accidentDescription', 'reportedToAuthorities', 'weatherConditions', 'weatherConditionsOther', 'roadConditions', 'roadConditionsOther', 'policeContacted', 'policeName', 'policeReportNumber', 'fireDepartmentContacted', 'fireDepartmentName', 'fireDepartmentReportNumber', 'ambulanceContacted', 'ambulanceName', 'ambulanceReportNumber', 'otherAuthorityContacted', 'otherAuthorityName', 'otherAuthorityReportNumber', 'thirdPartyDriverName', 'thirdPartyPlateNumber', 'thirdPartyInsuranceDetails']),
  'Vehicle Identification': new Set(['details_by_vehicle']),
  'Incident Specifics': new Set([]),
  'Injury & Liability': new Set(['werePedestriansInjured', 'pedestrians', 'wereWitnessesPresent', 'witnesses', 'wasNonPersonalPropertyDamaged', 'isPropertyCoveredUnderRoutesPolicy', 'additionalInformationNotes']),
  'Documents & Photos': new Set([]),
};

const REPORTED_FIELD_CATEGORY = Object.entries(REPORTED_OPEN_FORM_FIELDS).reduce((result, [category, fields]) => {
  fields.forEach((field) => { result[field] = category; });
  return result;
}, {});


const getReportedInformationSection = (key = '') => {
  return REPORTED_FIELD_CATEGORY[key] || null;
};

const formatDateValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '-';
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
  const dayFirstMatch = text.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dayFirstMatch) return `${dayFirstMatch[2]}/${dayFirstMatch[1]}/${dayFirstMatch[3]}`;
  const parsedDate = new Date(text);
  if (!Number.isNaN(parsedDate.getTime())) {
    return `${String(parsedDate.getMonth() + 1).padStart(2, '0')}/${String(parsedDate.getDate()).padStart(2, '0')}/${parsedDate.getFullYear()}`;
  }
  return text;
};

const PRIORITY_SOURCE_FIELDS = new Set([
  'accidentDate', 'accidentDateTime', 'date_of_loss', 'rentalAgreementNumber',
  'customerInsuranceCarrier', 'insuranceType', 'insuranceTypeOther', 'customerPolicyNumber',
  'thirdPartyInsuranceDetails', 'thirdPartyInsuranceNumber', 'preAuthorizationAmount',
  'accidentDescription', 'authorizedDriverName', 'driverAtTimeOfAccident',
  'driverAtTimeOfAccidentOther',
]);
const PRIORITY_VEHICLE_FIELDS = new Set([
  'unitFleetNumber', 'licensePlate', 'plateNumber', 'vin', 'year', 'make',
  'model', 'color', 'colour', 'odometerReading', 'mileage',
]);
const joinPriorityValues = (...values) => values
  .map((value) => String(value ?? '').trim())
  .filter(Boolean)
  .join(' / ') || '-';
const getPriorityReportedEntries = (claim = {}) => {
  const vehicle = Array.isArray(claim.details_by_vehicle) ? (claim.details_by_vehicle[0] || {}) : {};
  const renterInsurance = joinPriorityValues(claim.customerInsuranceCarrier, claim.insuranceTypeOther || claim.insuranceType, claim.customerPolicyNumber);
  const driverName = claim.driverAtTimeOfAccident === 'Other' ? claim.driverAtTimeOfAccidentOther : claim.driverAtTimeOfAccident;
  return [
    ['priorityDateOfLoss', formatDateValue(claim.accidentDate || claim.date_of_loss || claim.accidentDateTime)],
    ['priorityRaNumber', claim.rentalAgreementNumber],
    ['priorityUnitNumber', vehicle.unitFleetNumber],
    ['priorityPlateNumber', vehicle.licensePlate || vehicle.plateNumber],
    ['priorityVin', vehicle.vin],
    ['priorityYearMake', joinPriorityValues(vehicle.year, vehicle.make)],
    ['priorityModelColour', joinPriorityValues(vehicle.model, vehicle.color || vehicle.colour)],
    ['priorityMileage', vehicle.odometerReading || vehicle.mileage],
    ['priorityRenterInsurance', renterInsurance],
    ['priorityThirdPartyInsurance', joinPriorityValues(claim.thirdPartyInsuranceDetails, claim.thirdPartyInsuranceNumber)],
    ['priorityPreAuth', claim.preAuthorizationAmount],
    ['priorityDamage', vehicle.vehicleDamageDescription],
    ['priorityRenterDriverName', joinPriorityValues(claim.authorizedDriverName, driverName)],
  ];
};

const getReportedInformationSections = (claim = {}) => {
  // Keep the existing section metadata available for future edit/history views.
  void getReportedInformationSection;
  void PRIORITY_SOURCE_FIELDS;
  return [{ title: 'Priority Information', entries: getPriorityReportedEntries(claim) }];
};



const DETAIL_FIELD_LABELS = {
  priorityDateOfLoss: 'Date of Loss',
  priorityRaNumber: 'R/A No.',
  priorityUnitNumber: 'Unit No.',
  priorityPlateNumber: 'Plate No.',
  priorityVin: 'VIN',
  priorityYearMake: 'Year & Make',
  priorityModelColour: 'Model & Colour',
  priorityMileage: 'Mileage',
  priorityRenterInsurance: "Renter's Insurance",
  priorityThirdPartyInsurance: 'Third Party Insurance & Number',
  priorityPreAuth: 'Pre Auth',
  priorityDamage: 'Damage',
  priorityRenterDriverName: "Renter's/Driver's Name",
  insurance_number: 'Insurance Number',
  reportingEmployeeName: 'Reporting Employee Name',
  reportingEmployeeId: 'Reporting Employee ID',
  branchLocationCode: 'Branch / Location Code',
  reportDateTime: 'Report Date Time',
  reportDateTimeTimezone: 'Report Date Time Timezone',
  claimPriorityLevel: 'Claim Priority Level',
  claimPriorityLevelOther: 'Claim Priority Level - Other',
  tireGlassWaiver: 'Tire & Glass Waiver',
  claimSource: 'Claim Source',
  claimSourceOther: 'Claim Source - Other',
  rentalAgreementNumber: 'Rental Agreement Number',
  authorizedDriverName: 'Renter Driver Name',
  customerPhone: 'Renter Phone Number',
  email: 'Renter Email Address',
  customerReportedDateTime: 'Renter Reported Date & Time',
  customerReportedDateTimeTimezone: 'Renter Reported Date & Time Timezone',
  preAuthorizationAmount: 'Credit card pre-authorization/deposit taken',
  ldwStatus: 'Excess Damage Waiver (LDW/CDW) Status',
  ldwDeductibleAmount: 'LDW/CDW Deductible Amount',
  customerPolicyNumber: 'Policy Number',
  claimOwningBranch: 'Claim Owning Branch',
  rentalStartDate: 'Rental Start Date',
  rentalReturnDate: 'Rental Return Date',
  originalRentalReturnDate: 'Original Rental Return Date',
  insuranceType: 'Insurance Type',
  insuranceTypeOther: 'Insurance Type - Other',
  relationshipToClaim: 'Relationship To Claim',
  dealershipClaim: 'Dealership Claim',
  insuredCompanyName: 'Insured Company Name',
  insuredCompanyAddress: 'Insured Company Address',
  insuredCompanyPhone: 'Insured Company Phone',
  primaryContactName: 'Primary Contact Name',
  primaryContactEmail: 'Primary Contact Email',
  primaryContactPhone: 'Primary Contact Phone',
  accidentDateTime: 'Accident Date & Time',
  accidentDateTimeTimezone: 'Accident Date & Time Timezone',
  accidentDate: 'Accident Date',
  accidentTime: 'Accident Time',
  accidentLocation: 'Accident Location',
  accidentAddress: 'Accident Address',
  accidentCity: 'Accident City',
  accidentState: 'Accident State',
  accidentZipCode: 'Accident Zip Code',
  accidentDescription: 'Accident Description',
  gpsLatitude: 'GPS Latitude',
  gpsLongitude: 'GPS Longitude',
  reportedToAuthorities: 'Reported To Authorities',
  policeReportNumber: 'Police Report Number',
  weatherConditions: 'Weather Conditions',
  weatherConditionsOther: 'Weather Conditions - Other',
  roadConditions: 'Road Conditions',
  roadConditionsOther: 'Road Conditions - Other',
  injuriesReported: 'Injuries Reported',
  fatalitiesReported: 'Fatalities Reported',
  seriousInjuryDescription: 'Serious Injury Description',
  uploaded_files: 'Uploaded Files',
  created_datetime: 'Submitted On',
  claim_status: 'Claim Status',
  insurance_route: 'Insurance Opted For',
  appraisal_requested_date: 'Appraisal Requested Date',
  estimate_requested_date: 'Estimate Requested Date',
  insurance_claim_number: 'Claim #',
  adjuster_name: 'Adjuster Name',
  adjuster_phone: 'Adjuster Phone',
  adjuster_email: 'Adjuster Email Address',
  party_at_fault: 'Party at Fault',
  party_at_fault_name: 'Third Party Name',
  liability_accepted_by_insurance: 'Liability Accepted by Insurance',
  liability_insurance_name: 'Third Party Insurance Name',
  renter_insurance_type: "Renter's Insurance Type",
  renter_insurance_name: "Renter's Insurance Name",
  payer: 'Who is paying us?',
  payer_charged: 'Charged',
  payer_charged_amount: 'Charged Amount',
  demand_package: 'Demand Package',
  repairs_approved_at: 'Approved At',
  body_shop_name: 'Body Shop Name',
  amount_approved: 'Amount Approved',
  parts_cost_included: 'Parts Cost Included',
  vehicle_location: 'Vehicle Location',
  follow_up_entries: 'Follow Up',
  original_demand_amount: 'Original Demand Amount',
  settlement_offer_amount: 'Settlement Offer Amount',
  settlement_decision: 'Settlement Decision',
  team_lead_approval_status: 'Team Lead Approval',
  approved_by: 'Approved By',
  deductible_applied: 'Deductible Applied',
  deductible_applied_amount: 'Deductible Amount',
  charged_deductible: 'Charged Deductible',
  mode_of_payment: 'Mode of Payment',
  payment_amount: 'Payment Amount',
  check_received_date: 'Check Received Date',
  check_amount: 'Check Amount',
  check_received_insurance_company: 'Check Received by Which Ins Co',
  check1_received_date: 'Check 1 Received Date',
  check1_amount: 'Check 1 Amount',
  check1_received_insurance_company: 'Check 1 Received by Which Ins Co',
  check2_received_date: 'Check 2 Received Date',
  check2_amount: 'Check 2 Amount',
  check2_received_insurance_company: 'Check 2 Received by Which Ins Co',
  collections_reason: 'Collections Reason',
  collections_amount: 'Collections Amount',
  collections_date_added: 'Collections Date Added',
};

const getChangedFields = (beforeData, afterData) => {
  const beforeObject = beforeData && typeof beforeData === 'object' ? beforeData : {};
  const afterObject = afterData && typeof afterData === 'object' ? afterData : {};
  const ignoredKeys = new Set(['_id', 'created_datetime', 'updated_datetime', 'created_ip', 'updated_ip']);

  return Array.from(new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)]))
    .filter((key) => !ignoredKeys.has(key))
    .filter((key) => JSON.stringify(beforeObject[key]) !== JSON.stringify(afterObject[key]));
};

const getHistoryDescription = (log) => {
  const action = String(log?.action || '').toLowerCase();
  if (action === 'create') return 'Claim report created';
  if (action === 'update') return 'Claim report updated';
  if (action === 'move_to_claim_processing') return 'Claim moved to processing';
  if (action === 'claim_processing_update') return 'Claim processing updated';
  if (action === 'close') return 'Claim closed';
  if (action === 'delete') return 'Claim report deleted';
  return 'Claim report activity';
};

const getHistoryAdditionalInfo = (log) => {
  const action = String(log?.action || '').toLowerCase();

  if (action === 'create') {
    const afterData = log?.after_data || {};
    const items = [
      afterData.insurance_number ? `Insurance: ${afterData.insurance_number}` : '',
      afterData.branchLocationCode ? `Location: ${afterData.branchLocationCode}` : '',
      afterData.claimPriorityLevel ? `Priority: ${afterData.claimPriorityLevel}` : '',
    ].filter(Boolean);
    return items.join(' | ') || '-';
  }

  if (action === 'update' || action === 'claim_processing_update' || action === 'move_to_claim_processing' || action === 'move_back_to_reported' || action === 'close') {
    const changedFields = getChangedFields(log?.before_data, log?.after_data);
    return changedFields.length
      ? `Changed: ${changedFields.slice(0, 6).map((field) => DETAIL_FIELD_LABELS[field] || toTitleCase(field)).join(', ')}`
      : 'Updated record details';
  }

  return '-';
};

export default function SubmittedFormsPage({
  fetchEndpoint = '/get_submitted_open_forms',
  processingFetchEndpoint = '/get_claim_processing_forms',
  historyEndpointBase = '/open_form_event_logs',
  processingHistoryEndpointBase = '/claim_processing_event_logs',
  showProcessing = true,
  processingUpdateEndpointBase = '/update_claim_processing',
  processingCloseEndpointBase = '/move_claim_processing_to_closed_forms',
  processingMoveBackEndpointBase = '/move_claim_processing_back_to_reported',
  markViewedEndpointBase = '/mark_open_form_viewed',
  deleteReportedEndpointBase = '/delete_submitted_shuttle_bus_form',
  reportedLockWorkflow = 'Claims Reported by Location',
  processingLockWorkflow = 'Claims Processing',
  editFormProps = {},
  permissionKeys = SUBMITTED_FORMS_PERMISSION_KEYS,
  noFoundMessage = 'No submitted forms found for the selected location.',
  accessDeniedMessage = 'You do not have permission to view submitted forms.',
  historyTitlePrefix = 'Form History',
  historySubtitle = 'Create and edit activity for this submitted claim record.',
}) {
  const [rows, setRows] = useState([]);
  const [processingRows, setProcessingRows] = useState([]);
  const [locationColors, setLocationColors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(ALL_LOCATIONS);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyRecord, setHistoryRecord] = useState(null);
  const [selectedHistoryLog, setSelectedHistoryLog] = useState(null);
  const [activeSubPage, setActiveSubPage] = useState('reported');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openProcessDialog, setOpenProcessDialog] = useState(false);
  const [processRow, setProcessRow] = useState(null);
  const [processDialogTab, setProcessDialogTab] = useState('processing');
  const [processingForm, setProcessingForm] = useState(createProcessingForm());
  const [processingError, setProcessingError] = useState('');
  const [processingSaving, setProcessingSaving] = useState(false);
  const [documentsUpdating, setDocumentsUpdating] = useState(false);
  const [editingReportedInfo, setEditingReportedInfo] = useState(false);
  const [movingBackId, setMovingBackId] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = getLoggedInUser();
  const { acquireRecordLock, releaseRecordLock } = useRecordLock({ baseURL, userName: currentUser?.userName });
  const accessRole = currentUser?.accessRole || {};
  const canView = hasModulePermission(permissionKeys, 'View', accessRole);
  const submittedPermissionKeys = permissionKeys.filter((key) => /submi(?:t|te)d forms/i.test(String(key)));
  const scopedSubmittedPermissionKeys = submittedPermissionKeys.length ? submittedPermissionKeys : permissionKeys;
  const canProcessClaims = hasModulePermission(scopedSubmittedPermissionKeys, 'Edit', accessRole, false);
  const canEditDocuments = canProcessClaims || hasModulePermission(scopedSubmittedPermissionKeys, 'Document Edit', accessRole, false);
  const canUpdateReported = hasModulePermission(scopedSubmittedPermissionKeys, 'Update', accessRole, false);
  const canDeleteReported = hasModulePermission(scopedSubmittedPermissionKeys, 'Delete', accessRole, false);
  const canEditReported = canProcessClaims || canUpdateReported;
  const assignedLocations = useMemo(
    () => normalizeUserLocations(currentUser?.selected_locations || currentUser?.locations),
    [currentUser?.locations, currentUser?.selected_locations]
  );
  const dateRangeError = isInvalidDateRange(fromDate, toDate);

  const fetchSubmittedForms = () => {
    setLoading(true);
    setError('');

    return Promise.all([
      axios.get(`${baseURL}${fetchEndpoint}`, { params: getDateRangeParams(fromDate, toDate) }),
      processingFetchEndpoint ? axios.get(`${baseURL}${processingFetchEndpoint}`, { params: getDateRangeParams(fromDate, toDate) }) : Promise.resolve({ data: [] }),
      axios.get(`${baseURL}/get_locations`),
    ])
      .then(([openResponse, processingResponse, locationsResponse]) => {
        setRows(Array.isArray(openResponse?.data) ? openResponse.data : []);
        setProcessingRows(Array.isArray(processingResponse?.data) ? processingResponse.data : []);
        const colors = (Array.isArray(locationsResponse?.data) ? locationsResponse.data : []).reduce((acc, location) => {
          [location?.location_name, location?.code].filter(Boolean).forEach((key) => {
            acc[String(key).trim().toLowerCase()] = location?.claim_row_color || '';
          });
          return acc;
        }, {});
        setLocationColors(colors);
      })
      .catch(() => {
        setRows([]);
        setProcessingRows([]);
        setError('Unable to load submitted claim forms right now.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!canView) return;
    fetchSubmittedForms();
  }, [baseURL, canView, fetchEndpoint, processingFetchEndpoint, fromDate, toDate]);

  const scopedRows = useMemo(() => {
    if (assignedLocations.length === 0) {
      return [];
    }

    return rows.filter((row) => assignedLocations.includes(String(row?.branchLocationCode || '').trim()));
  }, [assignedLocations, rows]);

  const scopedProcessingRows = useMemo(() => {
    if (assignedLocations.length === 0) {
      return [];
    }

    return processingRows.filter((row) => assignedLocations.includes(String(row?.branchLocationCode || '').trim()));
  }, [assignedLocations, processingRows]);

  const locationOptions = useMemo(() => {
    const locationNames = [...scopedRows, ...scopedProcessingRows]
      .map((row) => String(row?.branchLocationCode || '').trim())
      .filter(Boolean);

    return [ALL_LOCATIONS, ...new Set(locationNames)];
  }, [scopedProcessingRows, scopedRows]);

  useEffect(() => {
    if (!locationOptions.includes(selectedLocation)) {
      setSelectedLocation(ALL_LOCATIONS);
    }
  }, [locationOptions, selectedLocation]);

  const filteredRows = useMemo(() => {
    const locationRows = selectedLocation === ALL_LOCATIONS
      ? scopedRows
      : scopedRows.filter((row) => String(row?.branchLocationCode || '').trim() === selectedLocation);

    if (dateRangeError) return [];

    return locationRows.filter((row) => isWithinDateRange(row?.created_datetime, fromDate, toDate));
  }, [dateRangeError, fromDate, scopedRows, selectedLocation, toDate]);

  const filteredProcessingRows = useMemo(() => {
    const locationRows = selectedLocation === ALL_LOCATIONS
      ? scopedProcessingRows
      : scopedProcessingRows.filter((row) => String(row?.branchLocationCode || '').trim() === selectedLocation);

    if (dateRangeError) return [];

    return locationRows.filter((row) => isWithinDateRange(row?.created_datetime, fromDate, toDate));
  }, [dateRangeError, fromDate, scopedProcessingRows, selectedLocation, toDate]);

  const handleOpenEdit = useCallback(async (row) => {
    if (!canView) return;
    const acquired = await acquireRecordLock(row._id, reportedLockWorkflow);
    if (!acquired) return;
    if (markViewedEndpointBase) axios.post(`${baseURL}${markViewedEndpointBase}/${row._id}`, { opened_by: currentUser?.userName }).then(() => window.dispatchEvent(new Event('claim-notifications-refresh'))).catch(() => {});
    setRows((prev) => prev.map((item) => String(item._id) === String(row._id) ? { ...item, opened_by_claims: true } : item));
    setProcessingRows((prev) => prev.map((item) => String(item._id) === String(row._id) ? { ...item, opened_by_claims: true } : item));
    setSelectedRow(row);
    setOpenEditDialog(true);
  }, [acquireRecordLock, baseURL, canView, currentUser?.userName, markViewedEndpointBase, reportedLockWorkflow]);

  const handleCloseEdit = () => {
    releaseRecordLock();
    setOpenEditDialog(false);
    setSelectedRow(null);
  };

  const handleOpenHistory = useCallback((row, historyType = 'open') => {
    setHistoryRecord(row);
    setOpenHistoryDialog(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryRows([]);

    const endpointBase = historyType === 'processing' ? processingHistoryEndpointBase : historyEndpointBase;

    axios.get(`${baseURL}${endpointBase}/${row._id}`)
      .then((response) => {
        setHistoryRows(Array.isArray(response?.data?.logs) ? response.data.logs : []);
      })
      .catch(() => {
        setHistoryRows([]);
        setHistoryError('Unable to load form history right now.');
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  }, [baseURL, historyEndpointBase, processingHistoryEndpointBase]);

  const handleCloseHistory = () => {
    setOpenHistoryDialog(false);
    setHistoryRows([]);
    setHistoryRecord(null);
    setHistoryError('');
    setHistoryLoading(false);
    setSelectedHistoryLog(null);
  };

  const handleOpenProcess = useCallback(async (row) => {
    const acquired = await acquireRecordLock(row._id, processingLockWorkflow);
    if (!acquired) return;
    if (markViewedEndpointBase) axios.post(`${baseURL}${markViewedEndpointBase}/${row._id}`, { opened_by: currentUser?.userName }).then(() => window.dispatchEvent(new Event('claim-notifications-refresh'))).catch(() => {});
    setRows((prev) => prev.map((item) => String(item._id) === String(row._id) ? { ...item, opened_by_claims: true } : item));
    setProcessingRows((prev) => prev.map((item) => String(item._id) === String(row._id) ? { ...item, opened_by_claims: true } : item));
    setProcessRow(row);
    setProcessDialogTab('processing');
    setProcessingForm(createProcessingForm(row));
    setProcessingError('');
    setOpenProcessDialog(true);
  }, [acquireRecordLock, baseURL, currentUser?.userName, markViewedEndpointBase, processingLockWorkflow]);

  const handleCloseProcess = () => {
    releaseRecordLock();
    setOpenProcessDialog(false);
    setProcessRow(null);
    setProcessDialogTab('processing');
    setProcessingForm(createProcessingForm());
    setProcessingError('');
    setProcessingSaving(false);
    setEditingReportedInfo(false);
  };

  const handleMoveBackToReported = useCallback(async (row) => {
    if (!window.confirm('Move this claim back to Claims Reported by Location?')) return;
    setMovingBackId(row._id);
    setError('');
    try {
      const response = await axios.put(`${baseURL}${processingMoveBackEndpointBase}/${row._id}`, { updated_by: currentUser?.userName });
      if (response?.data?.status_code === 0) throw new Error(response.data.message);
      await fetchSubmittedForms();
      setActiveSubPage('reported');
    } catch (moveError) {
      setError(moveError?.response?.data?.message || moveError?.message || 'Unable to move this claim back right now.');
    } finally {
      setMovingBackId(null);
    }
  }, [baseURL, currentUser?.userName, processingMoveBackEndpointBase]);

  const handleProcessingFieldChange = ({ target: { name, value } }) => {
    setProcessingForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'party_at_fault' && value !== '3rd Party' ? { party_at_fault_name: '' } : {}),
      ...(name === 'liability_accepted_by_insurance' && value !== "3rd Party's Personal Ins" ? { liability_insurance_name: '' } : {}),
      ...(name === 'liability_accepted_by_insurance' && value !== "Renter's Ins" ? { renter_insurance_type: '', renter_insurance_name: '' } : {}),
      ...(name === 'renter_insurance_type' && !value ? { renter_insurance_name: '' } : {}),
      ...(name === 'payer' && value !== 'Other' ? { payer_other: '' } : {}),
      ...(name === 'repairs_approved_at' && value !== 'Other' ? { repairs_approved_at_other: '' } : {}),
      ...(name === 'repairs_approved_at_2' && value !== 'Other' ? { repairs_approved_at_other_2: '' } : {}),
    }));
    setProcessingError('');
  };

  const handleDemandPackageChange = (key) => {
    setProcessingForm((prev) => ({
      ...prev,
      demand_package: {
        ...prev.demand_package,
        [key]: !prev.demand_package?.[key],
      },
    }));
  };

  const handleRemoveCheck2 = () => {
    setProcessingForm((prev) => ({
      ...prev,
      mode_of_payment: 'Check1',
      check2_received_date: '',
      check2_amount: '',
      check2_received_insurance_company: '',
    }));
  };

  const handleSaveProcessing = async (submitProcessing = false) => {
    if (!processRow?._id) return;

    if (submitProcessing) {
      const confirmation = await Swal.fire({
        icon: 'question',
        title: 'Submit Claims Processing?',
        text: 'Please confirm that the processing details are complete and ready to submit.',
        showCancelButton: true,
        confirmButtonText: 'Yes, submit',
        cancelButtonText: 'Cancel',
      });
      if (!confirmation.isConfirmed) return;
    }

    setProcessingSaving(true);
    setProcessingError('');

    const payload = { ...processingForm };
    if (payload.claim_status === 'Other') {
      payload.claim_status = String(payload.claim_status_other || '').trim() || 'Other';
    }
    delete payload.claim_status_other;
    if (payload.payer === 'Other') payload.payer = String(payload.payer_other || '').trim() || 'Other';
    delete payload.payer_other;
    if (payload.repairs_approved_at === 'Other') payload.repairs_approved_at = String(payload.repairs_approved_at_other || '').trim() || 'Other';
    delete payload.repairs_approved_at_other;
    if (payload.repairs_approved_at_2 === 'Other') payload.repairs_approved_at_2 = String(payload.repairs_approved_at_other_2 || '').trim() || 'Other';
    delete payload.repairs_approved_at_other_2;
    const followUpDate = String(processingForm.follow_up_date || '').trim();
    const followUpNotes = String(processingForm.follow_up_notes || '').trim();
    const internalNote = String(processingForm.internal_notes || '').trim();

    delete payload.follow_up_entries;
    delete payload.follow_up_date;
    delete payload.follow_up_notes;
    delete payload.internal_note_entries;
    delete payload.internal_notes;

    if (followUpDate || followUpNotes) {
      payload.new_follow_up_entry = {
        follow_up_date: followUpDate,
        notes: followUpNotes,
      };
    }

    if (internalNote) {
      payload.new_internal_note_entry = { note: internalNote };
    }

    const saveRequest = axios.post(`${baseURL}${processingUpdateEndpointBase}/${processRow._id}`, {
      ...payload,
      updated_by: currentUser?.userName,
    });

    const request = submitProcessing
      ? saveRequest.then(() => {
        const closePayload = { ...payload };
        delete closePayload.new_internal_note_entry;
        return axios.post(`${baseURL}${processingCloseEndpointBase}/${processRow._id}`, {
          ...closePayload,
          closed_by: currentUser?.userName,
          updated_by: currentUser?.userName,
        });
      })
      : saveRequest;

    request
      .then(() => {
        setProcessingForm((prev) => ({
          ...prev,
          internal_notes: '',
          internal_note_entries: internalNote
            ? [...(Array.isArray(prev.internal_note_entries) ? prev.internal_note_entries : []), {
              id: `saved-note-${Date.now()}`,
              note: internalNote,
              created_by: currentUser?.userName || '',
              created_datetime: new Date().toISOString(),
            }]
            : prev.internal_note_entries,
        }));
        fetchSubmittedForms();
        if (submitProcessing) {
          handleCloseProcess();
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Saved successfully',
            text: 'The claim processing details have been saved.',
            timer: 1800,
            showConfirmButton: false,
          });
        }
      })
      .catch(() => {
        setProcessingError(submitProcessing
          ? 'Unable to submit claim processing details right now.'
          : 'Unable to save claim processing details right now.');
      })
      .finally(() => setProcessingSaving(false));
  };

  const renderProcessInput = (label, name, props = {}) => (
    <label className={`claim-process-field ${props.full ? 'full' : ''}`.trim()}>
      <span>{label}</span>
      {props.type === 'date' || props.type === 'datetime' ? (
        <OpenFormDatePicker
          id={`claim-processing-${name}`}
          name={name}
          type={props.type}
          value={processingForm[name] || ''}
          onChange={handleProcessingFieldChange}
          readOnly={Boolean(props.readOnly)}
        />
      ) : props.multiline ? (
        <textarea
          name={name}
          value={processingForm[name] || ''}
          onChange={handleProcessingFieldChange}
          rows={props.rows || 4}
          placeholder={props.placeholder || label}
          spellCheck={Boolean(props.spellCheck)}
          lang={props.lang || undefined}
          autoCorrect={props.spellCheck ? 'on' : 'off'}
        />
      ) : (
        <input
          name={name}
          type={props.type || 'text'}
          onWheel={props.type === 'number' ? (event) => event.currentTarget.blur() : undefined}
          value={processingForm[name] || ''}
          onChange={handleProcessingFieldChange}
          placeholder={props.placeholder || label}
          readOnly={Boolean(props.readOnly)}
        />
      )}
    </label>
  );

  const renderProcessSelect = (label, name, options = []) => (
    <label className="claim-process-field">
      <span>{label}</span>
      <select name={name} value={processingForm[name] || ''} onChange={handleProcessingFieldChange}>
        {name === 'mode_of_payment' && processingForm[name] === 'Check' && (
          <option value="Check">Check</option>
        )}
        {options.map((option) => (
          <option key={option || 'blank'} value={option}>
            {option || 'Select'}
          </option>
        ))}
      </select>
    </label>
  );

  const updateProcessingDocuments = async (retainedFiles, newFiles = []) => {
    if (!canEditDocuments) {
      setProcessingError('You do not have permission to edit claim documents.');
      return;
    }
    if (!processRow?._id) return;
    setDocumentsUpdating(true);
    setProcessingError('');
    const formData = new FormData();
    formData.append('retainedUploadedFiles', JSON.stringify(retainedFiles));
    formData.append('updated_by', currentUser?.userName || '');
    Array.from(newFiles).forEach((file) => formData.append('uploadedFiles', file));
    try {
      const response = await axios.post(`${baseURL}/claim-uploads/shuttle-bus/${processRow._id}`, formData);
      const uploadedFiles = Array.isArray(response.data?.uploaded_files) ? response.data.uploaded_files : [];
      setProcessRow((prev) => ({ ...prev, uploaded_files: uploadedFiles }));
      fetchSubmittedForms();
    } catch (documentError) {
      setProcessingError('Unable to update uploaded documents right now.');
    } finally {
      setDocumentsUpdating(false);
    }
  };

  const handleDeleteProcessingDocument = async (fileToDelete) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete uploaded document?',
      text: 'This document will be removed from the claim.',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d32f2f',
    });
    if (!confirmation.isConfirmed) return;
    const retainedFiles = (processRow?.uploaded_files || []).filter((file) => file !== fileToDelete);
    updateProcessingDocuments(retainedFiles);
  };

  const renderInternalNoteLog = () => {
    const entries = sortFollowUpEntries(processingForm.internal_note_entries);

    return (
      <div className="claim-process-followups">
        {entries.length ? entries.map((entry, index) => (
          <div className="claim-process-followup-entry" key={entry.id || `${entry.created_datetime}-${index}`}>
            <div>
              <strong>{formatDateTime(entry.created_datetime)}</strong>
              <span>{entry.created_by ? `Saved by ${entry.created_by}` : 'Saved entry'}</span>
            </div>
            <RichTextContent value={entry.note} />
          </div>
        )) : (
          <div className="claim-process-empty-log">No internal comments saved yet.</div>
        )}
      </div>
    );
  };

  const handleDeleteReported = async (row) => {
    if (!canDeleteReported) return;
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete reported claim?',
      text: `This permanently deletes ${row?.insurance_number || `claim #${row?._id}`}.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (!confirmation.isConfirmed) return;
    try {
      await axios.delete(`${baseURL}${deleteReportedEndpointBase}/${row._id}`, {
        data: { deleted_by: currentUser?.userName || 'System' },
      });
      await fetchSubmittedForms();
      Swal.fire({ icon: 'success', title: 'Deleted', text: 'The reported claim was deleted.' });
    } catch (deleteError) {
      Swal.fire({ icon: 'error', title: 'Delete failed', text: deleteError?.response?.data?.message || 'Please try again.' });
    }
  };

  const reportedTableData = useMemo(() => ({
    columns: [
      { label: 'Sl.No', field: 'sl_no' },
      { label: 'Insurance Number', field: 'insurance_number' },
      { label: 'Rental Agreement Number', field: 'rental_agreement_number' },
      { label: 'Unit Number', field: 'unit_number' },
      { label: 'License Plate Number', field: 'license_plate_number' },
      { label: 'VIN Number', field: 'vin_number' },
      { label: 'Reported By', field: 'reporting_employee_name' },
      { label: 'Branch / Location', field: 'branch_location_code' },
      { label: 'Claim Owning Branch', field: 'claim_owning_branch' },
      { label: 'Authorized Driver', field: 'authorized_driver_name' },
      { label: 'Claim Priority', field: 'claim_priority_level' },
      { label: 'Claim Source', field: 'claim_source' },
      { label: 'Processing Status', field: 'processing_identifier' },
      { label: 'Accident Date', field: 'accident_date' },
      { label: 'Submitted On', field: 'created_datetime' },
      { label: 'Action', field: 'action' },
    ],
    rows: filteredRows.map((row, index) => {
      const completed = isClaimCompleted(row);
      const vehicle = Array.isArray(row?.details_by_vehicle) ? (row.details_by_vehicle[0] || {}) : {};
      const locationColor = locationColors[String(row?.branchLocationCode || '').trim().toLowerCase()];
      const rowColor = row?.opened_by_claims === false ? '#e5e7eb' : (locationColor || '#ffffff');

      return {
        _id: row?._id,
        _rowSx: {
          bgcolor: rowColor,
          '&.MuiTableRow-hover:hover': { bgcolor: rowColor, filter: 'brightness(0.97)' },
        },
        sl_no: index + 1,
        insurance_number: formatValue(row?.insurance_number),
        rental_agreement_number: formatValue(row?.rentalAgreementNumber),
        unit_number: formatValue(vehicle.unitFleetNumber || row?.unit),
        license_plate_number: formatValue(vehicle.licensePlate || vehicle.plateNumber || row?.licensePlate || row?.plateNumber),
        vin_number: formatValue(vehicle.vin || row?.vin),
        reporting_employee_name: formatValue(row?.reportingEmployeeName),
        branch_location_code: formatValue(row?.branchLocationCode),
        claim_owning_branch: formatValue(row?.claimOwningBranch),
        authorized_driver_name: formatValue(row?.authorizedDriverName),
        claim_priority_level: formatValue(row?.claimPriorityLevel),
        claim_source: formatValue(row?.claimSource),
        processing_identifier: (
          <span className={`ra-processing-badge ${getProcessingIdentifier(row).className}`}>
            {getProcessingIdentifier(row).label}
          </span>
        ),
        insurance_route: formatValue(row?.insurance_route),
        accident_date: formatDateValue(row?.accidentDate || row?.accidentDateTime),
        created_datetime: formatDateTime(row?.created_datetime),
        action: (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canView ? (
              <Button size="small" variant="contained" onClick={() => handleOpenEdit(row)}>
                {canEditReported && !completed ? 'Edit/View' : 'View'}
              </Button>
            ) : null}
            {canEditReported ? (
              <Button size="small" variant="text" onClick={() => handleOpenHistory(row)}>
                History
              </Button>
            ) : null}
            {canDeleteReported ? (
              <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteReported(row)}>
                Delete
              </Button>
            ) : null}
          </Stack>
        ),
      };
    }),
  }), [canDeleteReported, canEditReported, canView, filteredRows, handleOpenEdit, handleOpenHistory, locationColors]);

  const processingTableData = useMemo(() => ({
    columns: [
      { label: 'Sl.No', field: 'sl_no', sortable: true },
      { label: 'Insurance Number', field: 'insurance_number', sortable: true },
      { label: 'Rental Agreement Number', field: 'rental_agreement_number', sortable: true },
      { label: 'Unit Number', field: 'unit_number', sortable: true },
      { label: 'License Plate Number', field: 'license_plate_number', sortable: true },
      { label: 'VIN Number', field: 'vin_number', sortable: true },
      { label: 'Branch / Location', field: 'branch_location_code', sortable: true },
      { label: 'Authorized Driver', field: 'authorized_driver_name', sortable: true },
      {
        label: 'Loss Date',
        field: 'loss_date',
        sortable: true,
        getSortValue: (row) => row?.loss_date_sort,
      },
      {
        label: 'Submitted Date',
        field: 'submitted_date',
        sortable: true,
        getSortValue: (row) => row?.submitted_date_sort,
      },
      { label: 'Claim Status', field: 'claim_status', sortable: true },
      { label: 'Insurance Opted For', field: 'insurance_route' },
      { label: 'Party at Fault', field: 'party_at_fault' },
      { label: 'Payer', field: 'payer' },
      { label: 'Payment Mode', field: 'mode_of_payment' },
      { label: 'Processing Status', field: 'processing_identifier' },
      { label: 'Action', field: 'action' },
    ],
    rows: filteredProcessingRows.map((row, index) => {
      const completed = isClaimCompleted(row);
      const vehicle = Array.isArray(row?.details_by_vehicle) ? (row.details_by_vehicle[0] || {}) : {};
      const locationColor = locationColors[String(row?.branchLocationCode || '').trim().toLowerCase()];
      const rowColor = row?.opened_by_claims === false ? '#e5e7eb' : (locationColor || '#ffffff');

      return {
        _id: row?._id,
        _rowSx: {
          bgcolor: rowColor,
          '&.MuiTableRow-hover:hover': { bgcolor: rowColor, filter: 'brightness(0.97)' },
        },
        sl_no: index + 1,
        insurance_number: formatValue(row?.insurance_number),
        rental_agreement_number: formatValue(row?.rentalAgreementNumber),
        unit_number: formatValue(vehicle.unitFleetNumber || row?.unit),
        license_plate_number: formatValue(vehicle.licensePlate || vehicle.plateNumber || row?.licensePlate || row?.plateNumber),
        vin_number: formatValue(vehicle.vin || row?.vin),
        branch_location_code: formatValue(row?.branchLocationCode),
        authorized_driver_name: formatValue(row?.authorizedDriverName),
        loss_date: formatDateValue(row?.accidentDate || row?.accidentDateTime),
        loss_date_sort: new Date(row?.accidentDate || row?.accidentDateTime || '').getTime() || null,
        claim_status: formatValue(row?.claim_status),
        insurance_route: formatValue(row?.insurance_route),
        party_at_fault: formatValue(row?.party_at_fault),
        payer: formatValue(row?.payer),
        mode_of_payment: formatValue(row?.mode_of_payment),
        submitted_date: formatDateValue(row?.reported_claim?.created_datetime || row?.created_datetime),
        submitted_date_sort: new Date(row?.reported_claim?.created_datetime || row?.created_datetime || '').getTime() || null,
        processing_identifier: (
          <span className={`ra-processing-badge ${getProcessingIdentifier(row).className}`}>
            {getProcessingIdentifier(row).label}
          </span>
        ),
        action: (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canProcessClaims && !completed ? (
              <Button size="small" variant="outlined" className="ra-nowrap-btn ra-claim-processing-btn" onClick={() => handleOpenProcess(row)}>
                Claim Processing
              </Button>
            ) : null}
            {canEditDocuments && !canProcessClaims && !completed ? (
              <Button size="small" variant="outlined" className="ra-nowrap-btn" onClick={() => handleOpenProcess(row)}>Documents</Button>
            ) : null}
            {canProcessClaims && !completed ? (
              <Button size="small" color="warning" variant="outlined" className="ra-nowrap-btn" onClick={() => handleMoveBackToReported(row)} disabled={String(movingBackId) === String(row._id)}>
                {String(movingBackId) === String(row._id) ? 'Moving...' : 'Move Back'}
              </Button>
            ) : null}
            {canView ? (
              <Button size="small" variant="text" onClick={() => handleOpenHistory(row, 'processing')}>
                History
              </Button>
            ) : null}
          </Stack>
        ),
      };
    }),
  }), [canEditDocuments, canProcessClaims, canView, filteredProcessingRows, handleMoveBackToReported, handleOpenHistory, handleOpenProcess, locationColors, movingBackId]);

  if (!canView) {
    return <AccessDeniedState message={accessDeniedMessage} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 } }}>
      <Paper className="submitted-forms-master-tabs">
        <Tabs
          value={activeSubPage}
          onChange={(_, nextValue) => setActiveSubPage(nextValue)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="reported" label="Claims Reported by Location" />
          {showProcessing && <Tab value="processing" label="Claims Processing" />}
        </Tabs>
      </Paper>

      <AppDatatable
        title={activeSubPage === 'reported' ? 'Claims Reported by Location' : 'Claims Processing'}
        subtitle={activeSubPage === 'reported'
          ? 'View raw claim data submitted from assigned locations, edit submissions when allowed, and inspect history.'
          : 'Work the claim assessment, liability, payer, repairs, follow-up, settlement, deductible, payment, and collections steps.'}
        headerActions={(
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Box sx={{ minWidth: { xs: '100%', sm: 280 } }}>
              <Typography variant="caption" color="text.secondary">Location Filter</Typography>
              <FormControl size="small" fullWidth sx={{ mt: 0.25 }}>
                <Select
                  value={selectedLocation}
                  onChange={(event) => setSelectedLocation(event.target.value)}
                >
                  {locationOptions.map((locationName) => (
                    <MenuItem key={locationName} value={locationName}>
                      {locationName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', sm: 170 } }}>
              <Typography variant="caption" color={dateRangeError ? 'error' : 'text.secondary'}>From Date</Typography>
              <OpenFormDatePicker
                id="submitted-forms-from-date"
                name="fromDate"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                max={toDate || undefined}
                error={dateRangeError}
              />
            </Box>
            <Box sx={{ minWidth: { xs: '100%', sm: 170 } }}>
              <Typography variant="caption" color={dateRangeError ? 'error' : 'text.secondary'}>To Date</Typography>
              <OpenFormDatePicker
                id="submitted-forms-to-date"
                name="toDate"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                min={fromDate || undefined}
                error={dateRangeError}
              />
            </Box>
          </Stack>
        )}
        headerContent={(
          <>
            {!assignedLocations.length ? (
              <Typography sx={{ color: 'warning.main' }}>
                No assigned locations were found for this login user.
              </Typography>
            ) : null}
            {error ? <Typography sx={{ color: 'warning.main', mt: assignedLocations.length ? 0 : 1 }}>{error}</Typography> : null}
            {dateRangeError ? (
              <Typography sx={{ color: 'error.main', mt: 1 }}>
                To Date should not be less than From Date.
              </Typography>
            ) : null}
          </>
        )}
        data={activeSubPage === 'reported' ? reportedTableData : processingTableData}
        loading={loading}
        noFoundMessage={noFoundMessage}
      />

      <Dialog
        open={openEditDialog}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: 'calc(100vw - 24px)',
            maxWidth: '1800px',
            height: 'calc(100vh - 24px)',
            m: '12px',
          },
        }}
      >
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fbff' }}>
          {selectedRow ? (
            <EditSubmittedForm
              initialRow={selectedRow}
              readOnly={isClaimCompleted(selectedRow)}
              onCancel={handleCloseEdit}
              onSaved={() => {
                handleCloseEdit();
                fetchSubmittedForms();
              }}
              promoteToProcessingOnSubmit={fetchEndpoint === '/get_submitted_open_forms'}
              finalSubmitLabel={fetchEndpoint === '/get_submitted_open_forms' ? 'Claims Processing' : 'Save Changes'}
              {...editFormProps}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openProcessDialog}
        onClose={handleCloseProcess}
        fullWidth
        maxWidth={false}
        PaperProps={{
          className: 'claim-process-dialog',
        }}
      >
        <DialogTitle className="claim-process-title">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography className="claim-process-heading">
                Claim Processing
              </Typography>
              <Typography className="claim-process-subtitle">
                {processRow?.insurance_number ? `Insurance ${processRow.insurance_number}` : 'Track claim assessment, liability, payer, demand package, repairs, follow-up, settlement, payment, and collections.'}
              </Typography>
            </Box>
            <Chip
              label={formatValue(processRow?.branchLocationCode)}
              className="claim-process-location-chip"
            />
          </Stack>
          <Tabs value={processDialogTab} onChange={(_, value) => setProcessDialogTab(value)} sx={{ mt: 1.5, minHeight: 36 }}>
            <Tab value="processing" label="Claim Processing" sx={{ minHeight: 36 }} />
            <Tab value="reported" label="Claims Reported Information" sx={{ minHeight: 36 }} />
          </Tabs>
        </DialogTitle>
        <DialogContent dividers className="claim-process-content">
          {processingError ? <div className="claim-process-error">{processingError}</div> : null}

          {processDialogTab === 'reported' ? (editingReportedInfo ? (
            <EditSubmittedForm
              {...editFormProps}
              initialRow={{ ...(processRow?.reported_claim || {}), uploaded_files: processRow?.uploaded_files || [] }}
              readOnly={false}
              onCancel={() => setEditingReportedInfo(false)}
              onSaved={() => {
                setEditingReportedInfo(false);
                handleCloseProcess();
                fetchSubmittedForms();
              }}
              promoteToProcessingOnSubmit={false}
              finalSubmitLabel="Save Changes"
            />
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={() => setEditingReportedInfo(true)} disabled={!canEditReported}>
                  Edit Claims Reported Information
                </Button>
              </Stack>
              {getReportedInformationSections(processRow?.reported_claim).map((section, sectionIndex) => (
                <Paper className="claim-process-panel" key={section.title}>
                  <div className="claim-process-panel-head"><span>{String(sectionIndex + 1).padStart(2, '0')}</span><h3>{section.title}</h3></div>
                  <Grid container spacing={1.5} alignItems="stretch">
                    {section.title === 'Vehicle Identification' ? (
                      <Grid size={12}>
                        <Stack spacing={2}>
                          {(Array.isArray(processRow?.reported_claim?.details_by_vehicle)
                            ? processRow.reported_claim.details_by_vehicle
                            : []
                          ).map((vehicle, vehicleIndex) => (
                            <Paper key={vehicleIndex} variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, bgcolor: '#fbfdff', borderColor: '#cbd5e1' }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 1.5 }}>
                                <Typography sx={{ fontWeight: 800, color: '#173971' }}>Vehicle {vehicleIndex + 1}</Typography>
                                <Chip
                                  size="small"
                                  label={vehicle?.unitFleetNumber || vehicle?.vin || 'Vehicle details'}
                                  sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, fontWeight: 700 }}
                                />
                              </Stack>
                              <Grid container spacing={1.25} alignItems="stretch">
                                {Object.entries(vehicle || {})
                                  .filter(([key, value]) => (vehicleIndex > 0 || !PRIORITY_VEHICLE_FIELDS.has(key)) && formatDetailValue(value) !== '-')
                                  .sort(([leftKey], [rightKey]) => toTitleCase(leftKey).localeCompare(toTitleCase(rightKey)))
                                  .map(([key, value]) => (
                                    <Grid key={key} size={{ xs: 12, sm: 6, lg: 4 }}>
                                      <Box sx={{ p: 1.25, height: '100%', minHeight: 76, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, mb: 0.4 }}>
                                          {DETAIL_FIELD_LABELS[key] || toTitleCase(key)}
                                        </Typography>
                                        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#173971' }}>
                                          {formatDetailValue(value)}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  ))}
                              </Grid>
                            </Paper>
                          ))}
                        </Stack>
                      </Grid>
                    ) : section.entries.map(([key, value]) => (
                      <Grid key={key} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Box sx={{ p: 1.25, height: '100%', minHeight: 82, display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{DETAIL_FIELD_LABELS[key] || toTitleCase(key)}</Typography>
                          <Typography sx={{ mt: 0.4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{formatDetailValue(value)}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))}
            </Stack>
          )) : (
          <div className={`claim-process-layout${canProcessClaims ? '' : ' document-only'}`}>
            <Paper className="claim-process-panel claim-documents-panel" sx={{ gridColumn: '1 / -1', order: 99 }}>
              <div className="claim-process-panel-head">
                <span>11</span>
                <h3>Uploaded Documents</h3>
              </div>
              <Typography sx={{ mb: 1.25, color: '#9a6700', fontSize: 13, fontWeight: 700 }}>
                Note: Uploading or deleting a document is saved immediately. You do not need to Save or Submit the form.
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.25 }}>
                {canEditDocuments ? <Button variant="contained" component="label" disabled={documentsUpdating}>
                  {documentsUpdating ? 'Updating...' : 'Upload New Documents'}
                  <input
                    hidden
                    type="file"
                    multiple
                    onChange={(event) => {
                      const files = event.target.files;
                      if (files?.length) updateProcessingDocuments(processRow?.uploaded_files || [], files);
                      event.target.value = '';
                    }}
                  />
                </Button> : null}
              </Stack>
              {Array.isArray(processRow?.uploaded_files) && processRow.uploaded_files.length > 0 ? (
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end"><Button variant="outlined" onClick={() => viewAllUploadedFiles(baseURL, processRow.uploaded_files)}>View All</Button><Button variant="contained" onClick={() => window.open(`${baseURL}/claim-uploads/shuttle-bus/${processRow._id}/download-all`, '_blank', 'noopener,noreferrer')}>Download All as ZIP</Button></Stack>
                  {processRow.uploaded_files.map((file, index) => (
                    <Stack key={`${file?.saved_name || file?.original_name || 'document'}-${index}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ p: 1.25, border: '1px solid rgba(148, 163, 184, 0.3)', borderRadius: 2 }}>
                      <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{file?.original_name || file?.saved_name || `Document ${index + 1}`}</Typography>
                      <Stack direction="row" spacing={1} flexShrink={0}>
                        <Button size="small" variant="outlined" onClick={() => window.open(buildUploadedFileUrl(baseURL, file), '_blank', 'noopener,noreferrer')} disabled={!buildUploadedFileUrl(baseURL, file)}>View</Button>
                        <Button size="small" variant="contained" onClick={() => window.open(buildUploadedFileUrl(baseURL, file, true), '_blank', 'noopener,noreferrer')} disabled={!buildUploadedFileUrl(baseURL, file, true)}>Download</Button>
                        {canEditDocuments ? <Button size="small" variant="outlined" color="error" onClick={() => handleDeleteProcessingDocument(file)} disabled={documentsUpdating}>Delete</Button> : null}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">No documents were uploaded with this claim.</Typography>
              )}
            </Paper>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>02</span>
                <h3>Claim Assessment</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessInput('Rental Agreement Number', 'rentalAgreementNumber', { readOnly: true })}
                {renderProcessSelect('Claim Status', 'claim_status', ['', ...CLAIM_STATUS_OPTIONS])}
                {processingForm.claim_status === 'Other'
                  ? renderProcessInput('Other Claim Status', 'claim_status_other', { placeholder: 'Enter claim status' })
                  : null}
                {processingForm.claim_status === 'Appraisal Requested'
                  ? renderProcessInput('Appraisal Requested Date', 'appraisal_requested_date', { type: 'date' })
                  : null}
                {processingForm.claim_status === 'Estimate Requested'
                  ? renderProcessInput('Estimate Requested Date', 'estimate_requested_date', { type: 'date' })
                  : null}
                {renderProcessSelect('Insurance Opted For', 'insurance_route', ['', ...INSURANCE_ROUTE_OPTIONS])}
              </div>
            </Paper>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>03</span>
                <h3>Fault & Liability</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessSelect('Party at Fault', 'party_at_fault', ['', ...PARTY_AT_FAULT_OPTIONS])}
                {processingForm.party_at_fault === '3rd Party'
                  ? renderProcessInput('Third Party Name', 'party_at_fault_name')
                  : null}
                {renderProcessSelect('Liability Accepted by Insurance', 'liability_accepted_by_insurance', ['', ...LIABILITY_ACCEPTED_OPTIONS])}
                {processingForm.liability_accepted_by_insurance === "3rd Party's Personal Ins"
                  ? renderProcessInput('Third Party Insurance Name', 'liability_insurance_name')
                  : null}
                {processingForm.liability_accepted_by_insurance === "Renter's Ins"
                  ? renderProcessSelect("Renter's Insurance Type", 'renter_insurance_type', ['', ...RENTER_INSURANCE_TYPE_OPTIONS])
                  : null}
                {processingForm.liability_accepted_by_insurance === "Renter's Ins" && processingForm.renter_insurance_type
                  ? renderProcessInput(RENTER_INSURANCE_NAME_LABELS[processingForm.renter_insurance_type], 'renter_insurance_name')
                  : null}
                {renderProcessInput('Claim #', 'insurance_claim_number')}
                {renderProcessInput('Adjuster Name', 'adjuster_name')}
                {renderProcessInput('Adjuster Phone', 'adjuster_phone')}
                {renderProcessInput('Adjuster Email Address', 'adjuster_email', { type: 'email' })}
              </div>
            </Paper>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>04</span>
                <h3>Payer</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessSelect('Who is paying us?', 'payer', ['', ...PAYER_OPTIONS])}
                {processingForm.payer === 'Other' ? renderProcessInput('Specific Insurance Provider', 'payer_other') : null}
                {shouldShowPayerCharge(processingForm.payer)
                  ? renderProcessSelect('Charged', 'payer_charged', ['', 'Yes', 'No'])
                  : null}
                {shouldShowPayerCharge(processingForm.payer) && processingForm.payer_charged === 'Yes'
                  ? renderProcessInput('Amount', 'payer_charged_amount', { type: 'number' })
                  : null}
              </div>
            </Paper>

            <>
                <Paper className="claim-process-panel">
                  <div className="claim-process-panel-head">
                    <span>05</span>
                    <h3>Demand Package</h3>
                  </div>
                  <div className="claim-process-checklist">
                    {DEMAND_PACKAGE_ITEMS.map((item) => (
                      <label key={item.key} className="claim-process-check-row">
                        <input
                          type="checkbox"
                          checked={Boolean(processingForm.demand_package?.[item.key])}
                          onChange={() => handleDemandPackageChange(item.key)}
                        />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                </Paper>

                <Paper className="claim-process-panel">
                  <div className="claim-process-panel-head">
                    <span>06</span>
                    <h3>Repairs</h3>
                  </div>
                  <div className="claim-process-grid">
                    {renderProcessSelect('Approved At', 'repairs_approved_at', ['', ...REPAIRS_APPROVED_AT_OPTIONS])}
                    {processingForm.repairs_approved_at === 'Other' ? renderProcessInput('Other Approved At', 'repairs_approved_at_other') : null}
                    {processingForm.repairs_approved_at === 'Body Shop' ? renderProcessInput('Body Shop Name', 'body_shop_name') : null}
                    {processingForm.repairs_approved_at === 'Body Shop' ? renderProcessInput('Amount Approved', 'amount_approved', { type: 'number' }) : null}
                    {renderProcessInput('Parts Cost Included', 'parts_cost_included', { type: 'number' })}
                    {renderProcessSelect('Vehicle Location', 'vehicle_location', ['', ...VEHICLE_LOCATION_OPTIONS])}
                  </div>
                </Paper>

                <Paper className="claim-process-panel">
                  <div className="claim-process-panel-head">
                    <span>06B</span>
                    <h3>Repairs 2</h3>
                  </div>
                  <div className="claim-process-grid">
                    {renderProcessSelect('Approved At', 'repairs_approved_at_2', ['', ...REPAIRS_APPROVED_AT_OPTIONS])}
                    {processingForm.repairs_approved_at_2 === 'Other' ? renderProcessInput('Other Approved At', 'repairs_approved_at_other_2') : null}
                    {processingForm.repairs_approved_at_2 === 'Body Shop' ? renderProcessInput('Body Shop Name', 'body_shop_name_2') : null}
                    {processingForm.repairs_approved_at_2 === 'Body Shop' ? renderProcessInput('Amount Approved', 'amount_approved_2', { type: 'number' }) : null}
                    {renderProcessInput('Parts Cost Included', 'parts_cost_included_2', { type: 'number' })}
                    {renderProcessSelect('Vehicle Location', 'vehicle_location_2', ['', ...VEHICLE_LOCATION_OPTIONS])}
                  </div>
                </Paper>

                <Paper className="claim-process-panel">
                  <div className="claim-process-panel-head">
                    <span>07</span>
                    <h3>Settlement Offer</h3>
                  </div>
                  <div className="claim-process-grid">
                    {renderProcessSelect('Settlement Decision', 'settlement_decision', ['', 'Pending', 'Accepted', 'Negotiated'])}
                    {renderProcessInput('Original Demand Amount', 'original_demand_amount', { type: 'number' })}
                    {renderProcessInput('Settlement Offer Amount', 'settlement_offer_amount', { type: 'number' })}
                    {renderProcessSelect('Team Lead Approval', 'team_lead_approval_status', ['', 'Pending', 'Approved'])}
                    {renderProcessInput('Approved By', 'approved_by')}
                  </div>
                </Paper>
            </>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>08</span>
                <h3>Deductible Applied</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessSelect('Deductible Applied', 'deductible_applied', ['', 'Yes', 'No'])}
                {processingForm.deductible_applied === 'Yes'
                  ? renderProcessInput('Amount', 'deductible_applied_amount', { type: 'number' })
                  : null}
                {renderProcessSelect('Charged Deductible', 'charged_deductible', ['', 'Yes', 'No'])}
              </div>
            </Paper>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>09</span>
                <h3>Payment Verification</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessSelect('Mode of Payment', 'mode_of_payment', ['', ...PAYMENT_MODE_OPTIONS])}
                {shouldShowPaymentAmount(processingForm.mode_of_payment)
                  ? renderProcessInput('Amount', 'payment_amount', { type: 'number' })
                  : null}
                {showsCheck1(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 1 Received Date', 'check1_received_date', { type: 'date' })
                  : null}
                {showsCheck1(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 1 Amount', 'check1_amount', { type: 'number' })
                  : null}
                {showsCheck1(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 1 Received by Which Ins Co', 'check1_received_insurance_company', { full: true })
                  : null}
                {showsCheck2(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 2 Received Date', 'check2_received_date', { type: 'date' })
                  : null}
                {showsCheck2(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 2 Amount', 'check2_amount', { type: 'number' })
                  : null}
                {showsCheck2(processingForm.mode_of_payment)
                  ? renderProcessInput('Check 2 Received by Which Ins Co', 'check2_received_insurance_company', { full: true })
                  : null}
                {showsCheck2(processingForm.mode_of_payment) ? (
                  <Button color="error" variant="outlined" onClick={handleRemoveCheck2}>Remove Check 2</Button>
                ) : null}
              </div>
            </Paper>

            <Paper className="claim-process-panel">
              <div className="claim-process-panel-head">
                <span>10</span>
                <h3>Collections</h3>
              </div>
              <div className="claim-process-grid">
                {renderProcessSelect('Reason', 'collections_reason', ['', ...COLLECTION_REASON_OPTIONS])}
                {renderProcessInput('Amount', 'collections_amount', { type: 'number' })}
                {renderProcessInput('Date Added', 'collections_date_added', { type: 'date' })}
              </div>
            </Paper>

            <Paper className="claim-process-panel" sx={{ gridColumn: '1 / -1', order: -1 }}>
              <div className="claim-process-panel-head"><span>01</span><h3>Notes</h3></div>
              <div className="claim-process-grid">
                <div className="claim-process-field claim-process-field-full">
                  <span>Daily Work / Internal Comments</span>
                  <RichTextEditor value={processingForm.internal_notes} onChange={(value) => setProcessingForm((prev) => ({ ...prev, internal_notes: value }))} placeholder="Add internal notes and daily work updates…" />
                </div>
                <div className="claim-process-field claim-process-field-full">
                  <span>Comment History</span>
                  {renderInternalNoteLog()}
                </div>
              </div>
            </Paper>
          </div>
          )}
        </DialogContent>
        <DialogActions className="claim-process-actions">
          <Button variant="outlined" className="claim-process-cancel" onClick={handleCloseProcess}>{processDialogTab === 'reported' ? 'Close' : 'Cancel'}</Button>
          {processDialogTab === 'processing' && canProcessClaims ? <>
          <Button variant="outlined" className="claim-process-draft-save" onClick={() => handleSaveProcessing(false)} disabled={processingSaving}>
            {processingSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button variant="contained" className="claim-process-save" onClick={() => handleSaveProcessing(true)} disabled={processingSaving}>
            {processingSaving ? 'Processing...' : 'Claim Closure'}
          </Button>
          </> : null}
        </DialogActions>
      </Dialog>

      <Dialog open={openHistoryDialog} onClose={handleCloseHistory} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {historyRecord?.insurance_number ? `${historyTitlePrefix} - ${historyRecord.insurance_number}` : historyTitlePrefix}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                {historySubtitle}
              </Typography>
            </Box>
            <Chip
              label={`Record ID: ${formatValue(historyRecord?._id)}`}
              sx={{ bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#1d4ed8', fontWeight: 700 }}
            />
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f6f9fc' }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(148, 163, 184, 0.22)',
              boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
            }}
          >
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#dce9ff' }}>
                    {['ID', 'Type', 'Description', 'Event Date', 'Employee', 'Additional Information', 'Action'].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 800, color: '#173971', whiteSpace: 'nowrap' }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>Loading history...</Box>
                      </TableCell>
                    </TableRow>
                  ) : historyRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                          {historyError || 'No history found for this record.'}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    historyRows.map((log, index) => (
                      <TableRow
                        key={log._id || `history-${index}`}
                        sx={{ bgcolor: index % 2 === 0 ? '#ffffff' : '#f5f8ff' }}
                        hover
                      >
                        <TableCell>{formatValue(log._id)}</TableCell>
                        <TableCell>{String(log.action || '').toUpperCase() || '-'}</TableCell>
                        <TableCell>{getHistoryDescription(log)}</TableCell>
                        <TableCell>{formatDateTime(log.event_datetime)}</TableCell>
                        <TableCell>{formatValue(log.actor)}</TableCell>
                        <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {getHistoryAdditionalInfo(log)}
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" onClick={() => setSelectedHistoryLog(log)}>
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseHistory}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedHistoryLog)} onClose={() => setSelectedHistoryLog(null)} fullWidth maxWidth="lg">
        <DialogTitle>History Entry Details</DialogTitle>
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
            wrap="nowrap"
            sx={{
              alignItems: 'stretch',
            }}
          >
            <Grid
              item
              xs={6}
              sx={{
                flexBasis: '50%',
                maxWidth: '50%',
              }}
            >
              <Paper sx={{ p: 2, borderRadius: 3, bgcolor: '#fbfdff', border: '1px solid rgba(148, 163, 184, 0.18)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Before</Typography>
                <Stack spacing={1}>
                  {Object.entries(selectedHistoryLog?.before_data || {}).filter(([key]) => !DETAIL_DIALOG_EXCLUDED_KEYS.has(key)).map(([key, value]) => (
                    <Box key={`before-${key}`}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{DETAIL_FIELD_LABELS[key] || toTitleCase(key)}</Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formatDetailValue(value)}</Typography>
                    </Box>
                  ))}
                  {!selectedHistoryLog?.before_data ? <Typography color="text.secondary">No previous data.</Typography> : null}
                </Stack>
              </Paper>
            </Grid>
            <Grid
              item
              xs={6}
              sx={{
                flexBasis: '50%',
                maxWidth: '50%',
              }}
            >
              <Paper sx={{ p: 2, borderRadius: 3, bgcolor: '#fbfdff', border: '1px solid rgba(148, 163, 184, 0.18)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>After</Typography>
                <Stack spacing={1}>
                  {Object.entries(selectedHistoryLog?.after_data || {}).filter(([key]) => !DETAIL_DIALOG_EXCLUDED_KEYS.has(key)).map(([key, value]) => (
                    <Box key={`after-${key}`}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{DETAIL_FIELD_LABELS[key] || toTitleCase(key)}</Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{formatDetailValue(value)}</Typography>
                    </Box>
                  ))}
                  {!selectedHistoryLog?.after_data ? <Typography color="text.secondary">No updated data.</Typography> : null}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSelectedHistoryLog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
